import { forwardRef } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * Botón homologado del sistema (doc UX 20260627: "homologar tamaños, alturas y
 * estilos; revisar jerarquía entre acciones principales y secundarias").
 *
 * Variantes:
 *  - primary   acción principal de navegación/marca (azul NPMX).
 *  - success   confirmación / envío de solicitud (verde).
 *  - danger    acción destructiva o de rechazo.
 *  - secondary acción secundaria (contorno, sobre fondo claro).
 *  - ghost     acción terciaria sin contorno (back-links, toolbar).
 *
 * Tamaños con alturas fijas para que conviva alineado con inputs y selects.
 */
export type ButtonVariant = "primary" | "success" | "danger" | "secondary" | "ghost";
export type ButtonSize = "sm" | "md" | "lg";

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    "bg-primary text-white shadow-sm hover:bg-primary-hover focus-visible:ring-primary/40",
  success:
    "bg-action-ok text-white shadow-sm hover:bg-action-ok-hover focus-visible:ring-action-ok/40",
  danger:
    "bg-action-err text-white shadow-sm hover:bg-action-err-hover focus-visible:ring-action-err/40",
  secondary:
    "border border-edge-strong bg-surface text-fg-muted hover:bg-surface-2 hover:text-fg focus-visible:ring-edge-strong",
  ghost:
    "text-fg-subtle hover:bg-surface-2 hover:text-primary-strong focus-visible:ring-edge-strong",
};

const SIZES: Record<ButtonSize, string> = {
  sm: "h-8 gap-1.5 px-3 text-xs",
  md: "h-10 gap-2 px-4 text-sm",
  lg: "h-11 gap-2 px-5 text-sm",
};

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Muestra spinner y deshabilita; útil para mutaciones en curso. */
  loading?: boolean;
  /** Icono a la izquierda del texto (se oculta mientras `loading`). */
  icon?: React.ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "md", loading = false, icon, className, disabled, children, type, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? "button"}
      disabled={disabled || loading}
      className={cn(
        "inline-flex shrink-0 cursor-pointer items-center justify-center whitespace-nowrap rounded-md font-medium transition",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1",
        "disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none",
        "active:translate-y-px",
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : icon}
      {children}
    </button>
  );
});
